/**
 * AppointmentView — per `rcp-appointment-view.html`.
 *
 * A11y fixes (mockup #8):
 *   - `<time datetime="…">` on date badges for SR + machine reading.
 *   - `.ics` calendar link uses a proper `<a>` with descriptive `aria-label`.
 *   - Reschedule / Cancel are real `<button>`s.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { AppointmentDto } from '@retail-ops/shared-types';
import { getApiClient } from '../../services/apiClient';
import { useToast } from '../../components/a11y/AccessibleToast';
import { Calendar, MapPin, User, ExternalLink, X } from 'lucide-react';

export function AppointmentView(): JSX.Element {
  const [params] = useSearchParams();
  const id = params.get('id');
  const { pushToast } = useToast();
  const [appointments, setAppointments] = useState<ReadonlyArray<AppointmentDto>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        // TODO(PLANNED /portal/appointments).
        const res = await getApiClient().portal.appointments.list({ signal: controller.signal });
        if (res.success && res.data) setAppointments(res.data);
      } catch {
        /* handled by UI state */
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  async function cancel(a: AppointmentDto): Promise<void> {
    if (!confirm(`Cancel your ${new Date(a.startsAt).toLocaleString()} appointment?`)) return;
    try {
      const res = await getApiClient().portal.appointments.cancel(a.appointmentId, {
        reason: 'customer-request',
      });
      if (res.success) {
        pushToast('success', 'Appointment cancelled');
        setAppointments((cur) => cur.filter((x) => x.appointmentId !== a.appointmentId));
      } else {
        pushToast('error', 'Could not cancel', res.error ?? 'Please try again.');
      }
    } catch {
      pushToast('error', 'Network error', 'Please try again.');
    }
  }

  if (loading) return <p aria-live="polite">Loading your appointments...</p>;

  const selected = id ? appointments.find((a) => a.appointmentId === id) : appointments[0];

  if (!selected) {
    return (
      <section aria-labelledby="appt-heading">
        <h1 id="appt-heading" tabIndex={-1}>No appointments yet</h1>
        <p>
          You don&apos;t have any appointments scheduled.{' '}
          <a href="/apply/start">Start an application</a> — we&apos;ll help you book one if needed.
        </p>
      </section>
    );
  }

  const start = new Date(selected.startsAt);
  const month = start.toLocaleString(undefined, { month: 'short' });
  const day = String(start.getDate());
  const timeStr = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

  return (
    <section aria-labelledby="appt-heading">
      <h1 id="appt-heading" tabIndex={-1}>Your appointment</h1>

      <div className="card" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 20, alignItems: 'start' }}>
        <div
          aria-hidden="true"
          style={{
            width: 68,
            background: 'var(--pcb-blue)',
            color: '#fff',
            padding: 10,
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{month}</div>
          <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{day}</div>
        </div>
        <div>
          <h2 style={{ marginTop: 0 }}>{selected.serviceName ?? 'Appointment'}</h2>
          <p>
            <time dateTime={selected.startsAt}>
              {start.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })},{' '}
              {timeStr}
            </time>
          </p>
          <p>
            <MapPin aria-hidden="true" size={16} style={{ verticalAlign: '-3px' }} />{' '}
            {selected.branch ?? 'Phelps County Bank — branch TBD'}
          </p>
          {selected.staffName ? (
            <p>
              <User aria-hidden="true" size={16} style={{ verticalAlign: '-3px' }} />{' '}
              With {selected.staffName}
            </p>
          ) : null}
          {selected.notes ? <p>{selected.notes}</p> : null}

          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <a
              className="btn btn-secondary"
              href={`/portal/appointments/${selected.appointmentId}/ics`}
              aria-label={`Add ${selected.serviceName ?? 'appointment'} on ${start.toLocaleDateString()} to your calendar`}
            >
              <Calendar aria-hidden="true" size={16} />
              Add to calendar
            </a>
            <a
              className="btn btn-ghost"
              href={`/appointment/reschedule?id=${selected.appointmentId}`}
              aria-label={`Reschedule appointment on ${start.toLocaleDateString()}`}
            >
              <ExternalLink aria-hidden="true" size={16} />
              Reschedule
            </a>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void cancel(selected)}
              aria-label={`Cancel appointment on ${start.toLocaleDateString()}`}
            >
              <X aria-hidden="true" size={16} />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
